import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import Vue from 'vue';
import Meta from 'vue-meta';
import { createRouter } from './router.js';
import NoSsr from './components/no-ssr.js';
import NuxtChild from './components/nuxt-child.js';
import NuxtError from '../layouts/error.vue';
import Nuxt from './components/nuxt.js';
import App from './App.js';
import { setContext, getLocation, getRouteData, normalizeError } from './utils';
import { createStore } from './store.js';
/* Plugins */

import nuxt_plugin_sentryserver_7c63ddea from 'nuxt_plugin_sentryserver_7c63ddea'; // Source: ./sentry.server.js (mode: 'server')

import nuxt_plugin_sentryclient_16aa6c62 from 'nuxt_plugin_sentryclient_16aa6c62'; // Source: ./sentry.client.js (mode: 'client')

import nuxt_plugin_vuescrollto_5b418b23 from 'nuxt_plugin_vuescrollto_5b418b23'; // Source: ./vue-scrollto.js (mode: 'client')

import nuxt_plugin_cookieuniversalnuxt_17d39064 from 'nuxt_plugin_cookieuniversalnuxt_17d39064'; // Source: ./cookie-universal-nuxt.js (mode: 'all')

import nuxt_plugin_nuxtcustomheadersmiddleware445f2568_3c38a5e4 from 'nuxt_plugin_nuxtcustomheadersmiddleware445f2568_3c38a5e4'; // Source: ./nuxt-custom-headers.middleware.445f2568.js (mode: 'all')

import nuxt_plugin_recaptcha_28c0a0d6 from 'nuxt_plugin_recaptcha_28c0a0d6'; // Source: ./recaptcha.js (mode: 'all')

import nuxt_plugin_apollomodule_b3962c1c from 'nuxt_plugin_apollomodule_b3962c1c'; // Source: ./apollo-module.js (mode: 'all')

import nuxt_plugin_axios_a4a4b7b4 from 'nuxt_plugin_axios_a4a4b7b4'; // Source: ./axios.js (mode: 'all')

import nuxt_plugin_webfontloader_0f336426 from 'nuxt_plugin_webfontloader_0f336426'; // Source: ./webfontloader.js (mode: 'client')

import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80'; // Source: ../plugins/axios (mode: 'all')

import nuxt_plugin_lazysizes_63bafee8 from 'nuxt_plugin_lazysizes_63bafee8'; // Source: ../plugins/lazysizes (mode: 'client')

import nuxt_plugin_vuecarousel_6c010c3d from 'nuxt_plugin_vuecarousel_6c010c3d'; // Source: ../plugins/vue-carousel (mode: 'client')

import nuxt_plugin_smoothscroll_68d9071e from 'nuxt_plugin_smoothscroll_68d9071e'; // Source: ../plugins/smoothscroll (mode: 'client')

import nuxt_plugin_vue2touchevents_0aa7791c from 'nuxt_plugin_vue2touchevents_0aa7791c'; // Source: ../plugins/vue2-touch-events (mode: 'client')

import nuxt_plugin_globalmixins_415d51c6 from 'nuxt_plugin_globalmixins_415d51c6'; // Source: ../plugins/global-mixins (mode: 'all')

import nuxt_plugin_mdmicon_d6dbc0b4 from 'nuxt_plugin_mdmicon_d6dbc0b4'; // Source: ../plugins/mdm-icon (mode: 'all')

import nuxt_plugin_vuei18n_9dc1c90a from 'nuxt_plugin_vuei18n_9dc1c90a'; // Source: ../plugins/vue-i18n (mode: 'all')
// Component: <NoSsr>

Vue.component(NoSsr.name, NoSsr); // Component: <NuxtChild>

Vue.component(NuxtChild.name, NuxtChild);
Vue.component('NChild', NuxtChild); // Component NuxtLink is imported in server.js or client.js
// Component: <Nuxt>`

Vue.component(Nuxt.name, Nuxt); // vue-meta configuration

Vue.use(Meta, {
  keyName: 'head',
  // the component option name that vue-meta looks for meta info on.
  attribute: 'data-n-head',
  // the attribute name vue-meta adds to the tags it observes
  ssrAttribute: 'data-n-head-ssr',
  // the attribute name that lets vue-meta know that meta info has already been server-rendered
  tagIDKeyName: 'hid' // the property name that vue-meta uses to determine whether to overwrite or append a tag

});
const defaultTransition = {
  "name": "page",
  "mode": "",
  "appear": false,
  "appearClass": "appear",
  "appearActiveClass": "appear-active",
  "appearToClass": "appear-to"
};

function createApp(_x) {
  return _createApp.apply(this, arguments);
}

function _createApp() {
  _createApp = _asyncToGenerator(function* (ssrContext) {
    const router = yield createRouter(ssrContext);
    const store = createStore(ssrContext); // Add this.$router into store actions/mutations

    store.$router = router; // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141

    const registerModule = store.registerModule;

    store.registerModule = (path, rawModule, options) => registerModule.call(store, path, rawModule, Object.assign({
      preserveState: process.client
    }, options)); // Create Root instance
    // here we inject the router and store to all child components,
    // making them available everywhere as `this.$router` and `this.$store`.


    const app = _objectSpread({
      router,
      store,
      nuxt: {
        defaultTransition,
        transitions: [defaultTransition],

        setTransitions(transitions) {
          if (!Array.isArray(transitions)) {
            transitions = [transitions];
          }

          transitions = transitions.map(transition => {
            if (!transition) {
              transition = defaultTransition;
            } else if (typeof transition === 'string') {
              transition = Object.assign({}, defaultTransition, {
                name: transition
              });
            } else {
              transition = Object.assign({}, defaultTransition, transition);
            }

            return transition;
          });
          this.$options.nuxt.transitions = transitions;
          return transitions;
        },

        err: null,
        dateErr: null,

        error(err) {
          err = err || null;
          app.context._errored = Boolean(err);
          err = err ? normalizeError(err) : null;
          const nuxt = this.nuxt || this.$options.nuxt;
          nuxt.dateErr = Date.now();
          nuxt.err = err; // Used in src/server.js

          if (ssrContext) ssrContext.nuxt.error = err;
          return err;
        }

      }
    }, App); // Make app available into store via this.app


    store.app = app;
    const next = ssrContext ? ssrContext.next : location => app.router.push(location); // Resolve route

    let route;

    if (ssrContext) {
      route = router.resolve(ssrContext.url).route;
    } else {
      const path = getLocation(router.options.base);
      route = router.resolve(path).route;
    } // Set context to app.context


    yield setContext(app, {
      route,
      next,
      error: app.nuxt.error.bind(app),
      store,
      payload: ssrContext ? ssrContext.payload : undefined,
      req: ssrContext ? ssrContext.req : undefined,
      res: ssrContext ? ssrContext.res : undefined,
      beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
      ssrContext
    });

    const inject = function inject(key, value) {
      if (!key) throw new Error('inject(key, value) has no key provided');
      if (typeof value === 'undefined') throw new Error('inject(key, value) has no value provided');
      key = '$' + key; // Add into app

      app[key] = value; // Add into store

      store[key] = app[key]; // Check if plugin not already installed

      const installKey = '__nuxt_' + key + '_installed__';
      if (Vue[installKey]) return;
      Vue[installKey] = true; // Call Vue.use() to install the plugin into vm

      Vue.use(() => {
        if (!Vue.prototype.hasOwnProperty(key)) {
          Object.defineProperty(Vue.prototype, key, {
            get() {
              return this.$root.$options[key];
            }

          });
        }
      });
    };

    if (process.client) {
      // Replace store state before plugins execution
      if (window.__NUXT__ && window.__NUXT__.state) {
        store.replaceState(window.__NUXT__.state);
      }
    } // Plugin execution


    if (process.server && typeof nuxt_plugin_sentryserver_7c63ddea === 'function') {
      yield nuxt_plugin_sentryserver_7c63ddea(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_sentryclient_16aa6c62 === 'function') {
      yield nuxt_plugin_sentryclient_16aa6c62(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_vuescrollto_5b418b23 === 'function') {
      yield nuxt_plugin_vuescrollto_5b418b23(app.context, inject);
    }

    if (typeof nuxt_plugin_cookieuniversalnuxt_17d39064 === 'function') {
      yield nuxt_plugin_cookieuniversalnuxt_17d39064(app.context, inject);
    }

    if (typeof nuxt_plugin_nuxtcustomheadersmiddleware445f2568_3c38a5e4 === 'function') {
      yield nuxt_plugin_nuxtcustomheadersmiddleware445f2568_3c38a5e4(app.context, inject);
    }

    if (typeof nuxt_plugin_recaptcha_28c0a0d6 === 'function') {
      yield nuxt_plugin_recaptcha_28c0a0d6(app.context, inject);
    }

    if (typeof nuxt_plugin_apollomodule_b3962c1c === 'function') {
      yield nuxt_plugin_apollomodule_b3962c1c(app.context, inject);
    }

    if (typeof nuxt_plugin_axios_a4a4b7b4 === 'function') {
      yield nuxt_plugin_axios_a4a4b7b4(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_webfontloader_0f336426 === 'function') {
      yield nuxt_plugin_webfontloader_0f336426(app.context, inject);
    }

    if (typeof nuxt_plugin_axios_3566aa80 === 'function') {
      yield nuxt_plugin_axios_3566aa80(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_lazysizes_63bafee8 === 'function') {
      yield nuxt_plugin_lazysizes_63bafee8(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_vuecarousel_6c010c3d === 'function') {
      yield nuxt_plugin_vuecarousel_6c010c3d(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_smoothscroll_68d9071e === 'function') {
      yield nuxt_plugin_smoothscroll_68d9071e(app.context, inject);
    }

    if (process.client && typeof nuxt_plugin_vue2touchevents_0aa7791c === 'function') {
      yield nuxt_plugin_vue2touchevents_0aa7791c(app.context, inject);
    }

    if (typeof nuxt_plugin_globalmixins_415d51c6 === 'function') {
      yield nuxt_plugin_globalmixins_415d51c6(app.context, inject);
    }

    if (typeof nuxt_plugin_mdmicon_d6dbc0b4 === 'function') {
      yield nuxt_plugin_mdmicon_d6dbc0b4(app.context, inject);
    }

    if (typeof nuxt_plugin_vuei18n_9dc1c90a === 'function') {
      yield nuxt_plugin_vuei18n_9dc1c90a(app.context, inject);
    } // If server-side, wait for async component to be resolved first


    if (process.server && ssrContext && ssrContext.url) {
      yield new Promise((resolve, reject) => {
        router.push(ssrContext.url, resolve, () => {
          // navigated to a different route in router guard
          const unregister = router.afterEach(
          /*#__PURE__*/
          function () {
            var _ref = _asyncToGenerator(function* (to, from, next) {
              ssrContext.url = to.fullPath;
              app.context.route = yield getRouteData(to);
              app.context.params = to.params || {};
              app.context.query = to.query || {};
              unregister();
              resolve();
            });

            return function (_x2, _x3, _x4) {
              return _ref.apply(this, arguments);
            };
          }());
        });
      });
    }

    return {
      app,
      store,
      router
    };
  });
  return _createApp.apply(this, arguments);
}

export { createApp, NuxtError };